import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { priceFor, priceHintText, usePriceBook, fetchCatalogue, productLabel } from '../lib/pricing';
import { customerLine } from '../lib/customer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// An order line always points at a catalogue product; price_id records which
// price record produced the figure, and price_edited marks a line whose price
// staff typed by hand so re-pricing never overwrites it.
const blankItem = () => ({
  product_id: '',
  product_name: '',
  quantity: 1,
  unit_price: 0,
  price_id: null,
  price_edited: false
});

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const { priceBook, loadActivePrice } = usePriceBook();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    items: [blankItem()],
    status: 'pending',
    notes: ''
  });

  useEffect(() => {
    fetchOrders();
    fetchCustomers();
    fetchProducts();
  }, []);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchProducts = async () => {
    setProducts(await fetchCatalogue());
  };

  // Picking a product fills in its name and the price in force for the quantity
  // on the line; the price stays editable for one-off deals.
  const selectProduct = async (index, productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    const activePrice = await loadActivePrice(product.id);
    setFormData(prev => {
      const newItems = [...prev.items];
      const item = newItems[index];
      const { unit_price, price_id } = priceFor(product, activePrice, item.quantity);
      newItems[index] = {
        ...item,
        product_id: product.id,
        product_name: product.name,
        unit_price,
        price_id,
        price_edited: false
      };
      return { ...prev, items: newItems };
    });
  };

  // Puts a hand-edited line back on the catalogue price.
  const resetItemPrice = (index) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      const item = newItems[index];
      const product = products.find(p => p.id === item.product_id);
      if (!product) return prev;
      const { unit_price, price_id } = priceFor(product, priceBook[item.product_id], item.quantity);
      newItems[index] = { ...item, unit_price, price_id, price_edited: false };
      return { ...prev, items: newItems };
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, blankItem()]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      const numeric = field === 'quantity' || field === 'unit_price';
      let item = { ...newItems[index], [field]: numeric ? parseFloat(value) || 0 : value };
      // A new quantity can cross a wholesale tier, so re-price the line unless
      // staff have typed their own price on it.
      if (field === 'quantity' && item.product_id && !item.price_edited) {
        const product = products.find(p => p.id === item.product_id);
        const { unit_price, price_id } = priceFor(product, priceBook[item.product_id], item.quantity);
        item = { ...item, unit_price, price_id };
      }
      if (field === 'unit_price') item.price_edited = true;
      newItems[index] = item;
      return { ...prev, items: newItems };
    });
  };

  const priceHint = (item) =>
    priceHintText(products.find(p => p.id === item.product_id), priceBook[item.product_id], item.quantity);

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (formData.items.some(item => !item.product_id)) {
      toast.error('Please select a product for every item');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const orderData = {
        ...formData,
        // price_edited only steers the form; the saved line keeps the product
        // and price record it came from.
        items: formData.items.map(({ price_edited, ...item }) => item),
        total_amount: calculateTotal()
      };
      await axios.post(`${API}/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order created successfully');
      setOpen(false);
      resetForm();
      fetchOrders();
    } catch (error) {
      toast.error('Failed to create order');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      items: [blankItem()],
      status: 'pending',
      notes: ''
    });
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unknown';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.pending;
  };

  return (
    <div data-testid="orders-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Orders</h1>
          <p className="text-slate-600 mt-1">Manage customer orders</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-order-button" onClick={resetForm}>
              <Plus className="mr-2" size={16} />
              Create Order
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-dialog">
            <DialogHeader>
              <DialogTitle>Create New Order</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="customer">Customer *</Label>
                <Select value={formData.customer_id} onValueChange={(value) => setFormData({ ...formData, customer_id: value })}>
                  <SelectTrigger data-testid="customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customerLine(customer)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label>Order Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-button">
                    <Plus size={14} className="mr-1" /> Add Item
                  </Button>
                </div>
                {formData.items.map((item, index) => (
                  <div key={index} className="rounded-md border border-slate-200 p-2 space-y-1" data-testid={`order-item-${index}`}>
                    <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Select value={item.product_id} onValueChange={(value) => selectProduct(index, value)}>
                        <SelectTrigger data-testid={`product-select-${index}`}>
                          <SelectValue placeholder="Select product" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.length === 0 ? (
                            <div className="px-2 py-3 text-sm text-slate-500">No products available</div>
                          ) : (
                            products.map((product) => (
                              <SelectItem key={product.id} value={product.id}>
                                {productLabel(product)}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-24">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        min="1"
                        required
                      />
                    </div>
                    <div className="w-32">
                      <Input
                        type="number"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                    {formData.items.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(index)}>
                        <Trash2 size={16} />
                      </Button>
                    )}
                    </div>
                    {item.product_id && (
                      <div className="flex justify-between items-center gap-2 text-xs text-slate-500 px-1">
                        <span data-testid={`price-hint-${index}`}>{priceHint(item)}</span>
                        <span className="flex items-center gap-3 whitespace-nowrap">
                          {item.price_edited && (
                            <button
                              type="button"
                              className="text-blue-600 hover:underline"
                              onClick={() => resetItemPrice(index)}
                              data-testid={`reset-price-${index}`}
                            >
                              Use catalogue price
                            </button>
                          )}
                          <span className="font-medium text-slate-700">
                            Tsh {(item.quantity * item.unit_price).toLocaleString()}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  data-testid="notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Amount:</span>
                  <span data-testid="total-amount">Tsh {calculateTotal().toFixed(2)}</span>
                </div>
              </div>

              <Button data-testid="save-order-button" type="submit" className="w-full">
                Create Order
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order Number</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Total Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500">
                  No orders found. Create your first order.
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                  <TableCell className="font-medium">{order.order_number}</TableCell>
                  <TableCell>{getCustomerName(order.customer_id)}</TableCell>
                  <TableCell>{order.items.length} items</TableCell>
                  <TableCell>Tsh {order.total_amount.toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(order.status)} variant="outline">
                      {order.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
